import { ActionIcon, createStyles } from '@mantine/core';
import { IconChevronLeft, IconX } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';

import { useCreateGroupInvites } from '../../hooks/useCreateGroupInvites';
import { useCurrentUserChannel } from '../../hooks/useCurrentUserChannel';
import { evenRecipientSharesBps, exactPriceCents } from '../../utils/groupShareMath';

import { PickControlStep } from './PickControlStep';
import { SelectUsersStep } from './SelectUsersStep';
import { SetupGroupStep } from './SetupGroupStep';
import type { SelectableUser, GroupContestInfo, GroupRecipientDraft } from './types';

import dt from '../../testing.const';

const EMPTY_RECIPIENTS: readonly GroupRecipientDraft[] = [];

const useStyles = createStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
    padding: `${theme.other.spacing._16}px ${theme.other.spacing._24}px`,
  },
  headerTitleBlock: {
    flexGrow: 1,
    minWidth: 0,
  },
  headerTitleCentered: {
    textAlign: 'center',
  },
  howItWorksLink: {
    color: theme.colors.blue[7],
    textDecoration: 'none',
  },
  circleButton: {
    background: theme.colors.gray2[0],
    borderRadius: '50%',
    width: 36,
    height: 36,
    flexShrink: 0,
    color: theme.colors.gray2[6],
  },
}));

export type GroupEntryFlowStep = 'select-users' | 'setup-group' | 'pick-control';

export type GroupEntryFlowProps = {
  readonly entryId: string;
  readonly contest: GroupContestInfo;
  readonly headingId: string;
  readonly onSuccess: () => void;
  // Omitted by hosts with nothing to close back to, which hides the close control.
  readonly onClose?: () => void;
  readonly entryName?: string;
  readonly initialStep?: GroupEntryFlowStep;
  readonly initialRecipients?: readonly GroupRecipientDraft[];
  // Hosts that stay mounted through a close transition pass their open state so Escape disarms before unmount; others omit it and stay armed.
  readonly isEscapeArmed?: boolean;
};

export function GroupEntryFlow({
  entryId,
  contest,
  headingId,
  onSuccess,
  onClose,
  entryName,
  initialStep = 'select-users',
  initialRecipients = EMPTY_RECIPIENTS,
  isEscapeArmed = true,
}: GroupEntryFlowProps) {
  const { t } = useTranslation('group');
  const { classes, cx } = useStyles();
  const { user } = useContext(UserContext);
  const userId = user?.id;
  const { channel } = useCurrentUserChannel(userId);
  const [step, setStep] = useState<GroupEntryFlowStep>(initialStep);
  const [recipients, setRecipients] = useState<GroupRecipientDraft[]>([...initialRecipients]);
  const [pickControllerUserId, setPickControllerUserId] = useState<string | null>(null);
  // Hosts dismiss on onSuccess but not instantly, so submit must stay disabled or a second tap re-POSTs.
  const [hasSucceeded, setHasSucceeded] = useState(false);
  const submissionStarted = useRef(false);
  // Hosts remount a fresh flow per open; guards onSuccess after this instance unmounts.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const { mutateAsync: sendInvites, isLoading } = useCreateGroupInvites({
    userId,
    // Left empty: submit() below awaits sendInvites and calls onSuccess itself.
    onSuccess: () => {},
  });

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEscapeArmed && !isLoading) onClose?.();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isEscapeArmed, isLoading, onClose]);

  const toggleRecipient = useCallback(
    (selectedUser: SelectableUser) => {
      // Pick control can only sit with an invited recipient; removal hands it back to the organizer.
      setPickControllerUserId((controller) =>
        controller === selectedUser.userId ? null : controller
      );
      setRecipients((current) => {
        const existing = current.some((recipient) => recipient.userId === selectedUser.userId);
        const next = existing
          ? current.filter((recipient) => recipient.userId !== selectedUser.userId)
          : [...current, selectedUser];
        if (next.length === 0) return [];
        // Rebalance every recipient (cent-exact per head) — stamping only the new row over-allocates past 100%.
        const shares = evenRecipientSharesBps(next.length);
        return next.map((recipient, index) => ({
          ...recipient,
          shareBps: shares[index],
          priceCents: exactPriceCents(shares[index], contest.entryFeeCents),
        }));
      });
    },
    [contest.entryFeeCents]
  );

  const goToSetupGroup = useCallback(() => setStep('setup-group'), []);
  const goToPickControl = useCallback(() => setStep('pick-control'), []);
  const goBack = useCallback(
    () => setStep((current) => (current === 'pick-control' ? 'setup-group' : 'select-users')),
    []
  );

  const submit = useCallback(async () => {
    if (!userId || submissionStarted.current || isLoading) return;
    submissionStarted.current = true;
    try {
      await sendInvites({
        entryId,
        invites: recipients.map((recipient) => ({
          recipientUserId: recipient.userId,
          shareBps: recipient.shareBps,
          priceCents: recipient.priceCents,
        })),
        ...(pickControllerUserId ? { pickControllerRecipientUserId: pickControllerUserId } : {}),
      });
      if (isMounted.current) {
        setHasSucceeded(true);
        onSuccess();
      }
      // Success never re-arms: submissionStarted stays set so the invites cannot be re-POSTed mid-dismiss.
    } catch {
      // Reported by the mutation's onError; re-arm so a genuine retry works.
      submissionStarted.current = false;
    }
  }, [entryId, isLoading, onSuccess, pickControllerUserId, recipients, sendInvites, userId]);

  return (
    <>
      <div className={classes.header}>
        {step !== 'select-users' && (
          <ActionIcon
            aria-label={t('panel.back')}
            className={classes.circleButton}
            onClick={goBack}
            data-test-id={dt.panel.backButton}
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
        )}
        <div
          className={cx(
            classes.headerTitleBlock,
            step !== 'select-users' && classes.headerTitleCentered
          )}
        >
          <Title id={headingId} variant="title-x-large" tag="h2">
            {t('panel.title')}
          </Title>
          {step === 'select-users' && (
            <Text
              variant="subhead-small"
              component="a"
              className={classes.howItWorksLink}
              href={Routes.groupEntriesHowItWorks()}
              target="_blank"
              rel="noreferrer"
            >
              {t('panel.howItWorks')}
            </Text>
          )}
        </div>
        {onClose && (
          <ActionIcon
            aria-label={t('panel.close')}
            className={classes.circleButton}
            onClick={onClose}
            disabled={isLoading}
            data-test-id={dt.panel.closeButton}
          >
            <IconX size={18} />
          </ActionIcon>
        )}
      </div>
      {step === 'select-users' && (
        <SelectUsersStep
          currentUserId={userId}
          selectedUsers={recipients}
          onToggleUser={toggleRecipient}
          onContinue={goToSetupGroup}
        />
      )}
      {step === 'setup-group' && (
        // entryName is optional; hosts that only know ids omit it and the label row drops.
        <SetupGroupStep
          entryName={entryName}
          contest={contest}
          organizerHandle={channel?.handle}
          organizerAvatarUrl={channel?.avatarUrl}
          recipients={recipients}
          onRecipientsChange={setRecipients}
          onContinue={goToPickControl}
        />
      )}
      {step === 'pick-control' && (
        <PickControlStep
          entryName={entryName}
          contest={contest}
          organizerHandle={channel?.handle}
          organizerAvatarUrl={channel?.avatarUrl}
          recipients={recipients}
          pickControllerUserId={pickControllerUserId}
          onPickControllerChange={setPickControllerUserId}
          onSubmit={submit}
          isSubmitting={isLoading}
          hasSucceeded={hasSucceeded}
        />
      )}
    </>
  );
}
